import React, { useEffect, useState } from "react";
import { Table } from "../../components/Table";
import PageTitle from "../../components/PageTitle";
import { columns, data } from "../../utils/constants";
import {
  deleteNotices,
  fetchData,
  getNoticeListData,
  postData,
} from "../../api/apiService";
import { ServiceType } from "../../utils/enums";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import TenantLink from "../../components/TenantLink";
import { Breadcrumb, Button, Modal, Popover, Select, Tooltip } from "antd";
import { FaFilter, FaList, FaPlus } from "react-icons/fa";
import { FaArrowsRotate } from "react-icons/fa6";
import { CloseOutlined, DeleteFilled } from "@ant-design/icons";
import BulkStatusButton from "../../components/BulkStatusButton";
import { toast } from "react-toastify";
import WarningImage from "../../assets/images/Warning.png";
import moment from "moment";
import { useAxiosPost } from "../../hooks/useAxios";
import DashboardButton from "../../components/DashboardButton";
import TableV2 from "../../components/TableV2";
import DownloadDataButton from "../../components/DownloadDataButton";
import { filter } from "lodash";
import { DOCUMENTS } from "../../api/endpointMap";
import UserFilterDropdown, {
  ALL_USERS,
  toQueryParam as userFilterToQueryParam,
} from "../../components/UserFilterDropdown";

export const ResponseOutstandings = ({ serviceType }) => {
  const [responseOutstandingList, setResponseOutstandingList] = useState([]);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState([]);
  const [loading, setLoading] = useState(true);
  // User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). ALL_USERS sentinel
  // → omit assigned_user_id from the payload; BE applies role-default scope.
  const [userFilter, setUserFilter] = useState(ALL_USERS);
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [filterOptions, setFilterOptions] = useState({
    assessmentYear: [],
    sectionCode: [],
    rectificationRights: [],
    responseType: [],
    clientName: [],
    demandReferenceNo: [],
  });

  const [activeFilters, setActiveFilters] = useState({
    assessmentYear: true,
    sectionCode: true,
    rectificationRights: true,
    responseType: true,
    clientName: true,
    demandReferenceNo: true,
  });

  const [filterValues, setFilterValues] = useState({
    assessmentYear: null,
    sectionCode: null,
    rectificationRights: null,
    responseType: null,
    clientName: null,
    demandReferenceNo: null,
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchClientType = queryParams.get("noticeType") ?? "active";
  const clientName = queryParams.get("client_name");

  const [limitStart, setLimitStart] = useState(0);
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  const [ftechOutstandingscount] = useAxiosPost(
    DOCUMENTS.GET_COUNT
  );

  const { Option } = Select;

  const buildBackendFilters = () => {
    const filters = [["disabled", "=", searchClientType === "active" ? 0 : 1]];

    if (clientId) {
      filters.push(["client", "=", clientId]);
    }

    if (activeFilters.assessmentYear && filterValues.assessmentYear) {
      filters.push([
        "assessment_year",
        "like",
        `%${filterValues.assessmentYear}%`,
      ]);
    }

    if (activeFilters.sectionCode && filterValues.sectionCode) {
      filters.push(["section_code", "like", `%${filterValues.sectionCode}%`]);
    }

    if (activeFilters.rectificationRights && filterValues.rectificationRights) {
      filters.push([
        "rectification_rights",
        "like",
        `%${filterValues.rectificationRights}%`,
      ]);
    }

    if (activeFilters.responseType && filterValues.responseType) {
      filters.push(["response_type", "like", `%${filterValues.responseType}%`]);
    }

    if (activeFilters.clientName && filterValues.clientName) {
      filters.push(["client_name", "like", `%${filterValues.clientName}%`]);
    }

    if (activeFilters.demandReferenceNo && filterValues.demandReferenceNo) {
      filters.push([
        "demand_reference_no",
        "like",
        `%${filterValues.demandReferenceNo}%`,
      ]);
    }

    return filters;
  };

  const getResponseoutstandingscount = () => {
    const filters = buildBackendFilters();

    ftechOutstandingscount({
      payload: {
        doctype: "Response to Outstanding Demand",
        filters: filters,
      },
      cb: (data) => {
        setTotalRecords(data?.message);
      },
    });
  };

  const getInitialFilterOptions = async () => {
    try {
      const data = await getNoticeListData("Response to Outstanding Demand", {
        filters: JSON.stringify({
          client: clientId,
          disabled: searchClientType == "active" ? 0 : 1,
        }),
        fields: JSON.stringify([
          "assessment_year",
          "section_code",
          "rectification_rights",
          "response_type",
          "client_name",
          "demand_reference_no",
        ]),
        limit: 1000,
      });

      if (data?.data && data?.data?.length > 0) {
        const uniqueYears = [
          ...new Set(data.data.map((item) => item.assessment_year)),
        ].filter(Boolean);
        const uniqueSectionCodes = [
          ...new Set(data.data.map((item) => item.section_code)),
        ].filter(Boolean);
        const uniqueRectificationRights = [
          ...new Set(data.data.map((item) => item.rectification_rights)),
        ].filter(Boolean);
        const uniqueResponseTypes = [
          ...new Set(data.data.map((item) => item.response_type)),
        ].filter(Boolean);
        const uniqueClientNames = [
          ...new Set(data.data.map((item) => item.client_name)),
        ].filter(Boolean);
        const uniqueDemandRefNos = [
          ...new Set(data.data.map((item) => item.demand_reference_no)),
        ].filter(Boolean);

        setFilterOptions({
          assessmentYear: uniqueYears,
          sectionCode: uniqueSectionCodes,
          rectificationRights: uniqueRectificationRights,
          responseType: uniqueResponseTypes,
          clientName: uniqueClientNames,
          demandReferenceNo: uniqueDemandRefNos,
        });
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  // Fetch ALL demands matching current filters — used by TableV2
  // PDF/CSV download buttons. Backend caps page_length at 500 so loop
  // in batches. Mirrors getResponseOutstandingList params/endpoint.
  const fetchAllForExport = async () => {
    const PAGE_SIZE = 500;
    const all = [];
    let start = 0;
    while (true) {
      const params = { start, page_length: PAGE_SIZE };
      if (clientId) params.client = clientId;
      if (activeFilters.clientName && filterValues.clientName) params.client_name = filterValues.clientName;
      if (activeFilters.assessmentYear && filterValues.assessmentYear) params.assessment_year = filterValues.assessmentYear;
      if (activeFilters.demandReferenceNo && filterValues.demandReferenceNo) params.demand_reference_no = filterValues.demandReferenceNo;
      if (activeFilters.sectionCode && filterValues.sectionCode) params.section_code = filterValues.sectionCode;
      if (activeFilters.rectificationRights && filterValues.rectificationRights) params.rectification_rights = filterValues.rectificationRights;
      if (activeFilters.responseType && filterValues.responseType) params.response_type = filterValues.responseType;
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const resp = await fetchData("/api/v1/income-tax/demands", params);
      const batch = resp?.data?.records || [];
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      start += PAGE_SIZE;
    }
    return all;
  };

  const getResponseOutstandingList = async (start, itemsPerPage) => {
    setLoading(true);

    try {
      // Migrated from legacy Frappe `getNoticeListData("Response to Outstanding
      // Demand", ...)` call which resolved to `GET <base>/Response to Outstanding
      // Demand` — that path returned 404 on the FastAPI backend, so the list
      // was always empty after redirect from AddResponseOutstandingsForm.
      // Now calls the real FastAPI list endpoint (same one Eproceedings.jsx's
      // Response to Outstanding Demand tab uses), mapping column-level filter
      // state → query params.
      const params = {
        start: start,
        page_length: itemsPerPage,
      };
      if (clientId) params.client = clientId;
      // Backend `/api/v1/income-tax/demands` always filters `disabled=False`
      // (active notices). `notice_mode` is for preset filters only (over_due,
      // open, etc.) — URL's `?noticeType=active|inactive` has no backend
      // equivalent here, so don't pass it.

      if (activeFilters.clientName && filterValues.clientName) {
        params.client_name = filterValues.clientName;
      }
      if (activeFilters.assessmentYear && filterValues.assessmentYear) {
        params.assessment_year = filterValues.assessmentYear;
      }
      if (activeFilters.demandReferenceNo && filterValues.demandReferenceNo) {
        params.demand_reference_no = filterValues.demandReferenceNo;
      }
      if (activeFilters.sectionCode && filterValues.sectionCode) {
        params.section_code = filterValues.sectionCode;
      }
      if (activeFilters.rectificationRights && filterValues.rectificationRights) {
        params.rectification_rights = filterValues.rectificationRights;
      }
      if (activeFilters.responseType && filterValues.responseType) {
        params.response_type = filterValues.responseType;
      }

      // §7.2: scope to a specific user / Unassigned / All. ALL_USERS
      // → omit param entirely so BE auto-scopes per role.
      const userParam = userFilterToQueryParam(userFilter);
      if (userParam !== undefined) params.assigned_user_id = userParam;

      const data = await fetchData("/api/v1/income-tax/demands", params);

      const fetchedData = data?.data?.records || [];
      const dataWithType = fetchedData.map(item => ({ ...item, notice_type: "Response to Outstanding Demand" }));
      setResponseOutstandingList(dataWithType);
      setTotalRecords(data?.data?.total_records || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      setResponseOutstandingList([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const handleDeleteNotices = async () => {
    try {
      // Legacy `/api/v1/documents/fast-delete` returned 404 — use the
      // portal-scoped `/api/v1/income-tax/delete-documents` endpoint.
      const response = await deleteNotices(
        DOCUMENTS.DELETE_NOTICES_INCOME_TAX,
        {
          doctype: "Response to Outstanding Demand",
          docnames: JSON.stringify(selectedNoticeIds),
        }
      );
      toast.info("Deleted Successfully");
    } catch (err) {
      toast.error(err);
    } finally {
      getResponseOutstandingList(limitStart, limit);
      setSelectedNoticeIds([]);
    }
  };

  const handleOk = () => {
    setIsDeleteModalOpen(false);
    handleDeleteNotices();
  };

  const handleCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const toggleFilter = (filterName) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleFilterValueChange = (filterName, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const applyFilters = () => {
    setLimitStart(0);
    getResponseOutstandingList(0, limit);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setLimitStart(0);
    getResponseOutstandingList(0, newLimit);
  };

  const clearFilters = () => {
    setFilterValues({
      assessmentYear: null,
      sectionCode: null,
      rectificationRights: null,
      responseType: null,
      clientName: null,
      demandReferenceNo: null,
    });
    setLimitStart(0);
    getResponseOutstandingList(0, limit);
  };

  useEffect(() => {
    applyFilters();
    // userFilter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2): re-fetch on change.
  }, [
    filterValues.assessmentYear,
    filterValues.sectionCode,
    filterValues.rectificationRights,
    filterValues.responseType,
    filterValues.clientName,
    filterValues.demandReferenceNo,
    userFilter,
  ]);

  useEffect(() => {
    getInitialFilterOptions().then(() => {
      getResponseOutstandingList(limitStart, limit);
    });
  }, []);

  const columns = [
    {
      label: "Client Name",
      key: "client_name",
      filterable: true,
      filterType: "autocomplete",
      filterName: "client_name",
    },
    {
      label: "Demand Reference No",
      key: "demand_reference_no",
      filterable: true,
      filterType: "text",
      filterName: "demandReferenceNo",
    },
    {
      label: "Section Code",
      key: "section_code",
      filterable: true,
      filterType: "text",
      filterName: "sectionCode",
    },
    {
      label: "Assessment Year",
      key: "assessment_year",
      filterable: true,
      filterType: "text",
      filterName: "assessmentYear",
    },
    {
      label: "Rectification Rights",
      key: "rectification_rights",
      filterable: true,
      filterType: "text",
      filterName: "rectificationRights",
    },
    {
      label: "Response Type",
      key: "response_type",
      filterable: true,
      filterType: "text",
      filterName: "responseType",
    },
  ];

  const filterConfig = [
    { name: "clientName", label: "Client Name", type: "select" },
    { name: "demandReferenceNo", label: "Demand Reference No", type: "select" },
    { name: "sectionCode", label: "Section Code", type: "select" },
    { name: "assessmentYear", label: "Assessment Year", type: "select" },
    {
      name: "rectificationRights",
      label: "Rectification Rights",
      type: "select",
    },
    { name: "responseType", label: "Response Type", type: "select" },
  ];

  return (
    <>
      <div className="w-full flex items-center justify-between">
        {clientId ? (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin/clients"
                          : serviceType == "tds"
                          ? "/tds/clients"
                          : "/income-tax/clients",
                    }}
                  >
                    Clients{" "}
                  </TenantLink>
                ),
              },
              {
                title: (
                  <Link className="exempt-from-readonly" to={-1}>
                    {clientName || "Client Details"}
                  </Link>
                ),
              },
              { title: `Notices` },
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        ) : (
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname:
                        serviceType == "gstin"
                          ? "/gstin"
                          : serviceType == "tds"
                          ? "/tds"
                          : "/income-tax",
                    }}
                  >
                    {serviceType == "gstin"
                      ? "GST "
                      : serviceType == "tds"
                      ? "TDS"
                      : "Income Tax"}
                  </TenantLink>
                ),
              },

              { title: `Notices` },
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        )}
        <DashboardButton serviceType={serviceType} />
      </div>

      <PageTitle title="Response to Outstanding Demand" />

      {searchClientType == "active" && (
        <div className="flex flex-wrap items-center w-full justify-end gap-2 pr-4 mt-4">
          {selectedNoticeIds.length == 0 ? (
            <Tooltip title="Please select a notice">
              <Button
                type="primary"
                icon={<DeleteFilled />}
                className="mr-2"
                disabled
              >
                Delete Response Outstanding
              </Button>
            </Tooltip>
          ) : (
            <Button
              type={"primary"}
              icon={<DeleteFilled />}
              onClick={() => setIsDeleteModalOpen(true)}
              className="mr-2"
              disabled={selectedNoticeIds.length > 0 ? false : true}
            >
              Delete Response Outstanding
            </Button>
          )}
          <BulkStatusButton
            selectedIds={selectedNoticeIds}
            doctype="Response to Outstanding Demand"
            noun="demand"
            onDone={() => {
              getResponseOutstandingList(limitStart, limit);
              setSelectedNoticeIds([]);
            }}
          />

          <Button
            onClick={() => {
              navigate(`/${serviceType}/add-responseoutstandings/${clientId}`);
            }}
            variant="outlined"
            icon={<FaPlus />}
            type="primary"
          >
            Add Response To Outstanding Demand
          </Button>
          <div>
            <Button
              type="primary"
              icon={<FaArrowsRotate />}
              onClick={() => {
                getResponseOutstandingList(limitStart, limit);
              }}
            >
              Refresh
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {/* <Button
              type={showFilters ? "primary" : "default"}
              icon={<FaFilter />}
              onClick={() => setShowFilters(!showFilters)}
              className="mr-2"
              style={{
                backgroundColor: showFilters ? "#6c5ce7" : "",
                borderColor: showFilters ? "#6c5ce7" : "",
              }}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button> */}

            {/* <Button
              onClick={clearFilters}
              className="ml-2 flex items-center gap-2"
            >
              <CloseOutlined /> Clear Filters
            </Button> */}
          </div>
        </div>
      </div>

      {/* Filter Options Row */}
      {showFilters && (
        <div className="mt-4">
          {/* <div className="flex flex-wrap gap-2 ">
            {filterConfig.map((filter) => (
              <Button
                key={filter.name}
                type={activeFilters[filter.name] ? "primary" : "default"}
                onClick={() => toggleFilter(filter.name)}
                style={{
                  backgroundColor: activeFilters[filter.name] ? "#6c5ce7" : "",
                  borderColor: activeFilters[filter.name] ? "#6c5ce7" : "",
                }}
              >
                {filter.label}
              </Button>
            ))}
          </div>
          <span className="text-gray-500 text-sm">
            (All filters are active by default. Click to deactivate/activate)
          </span> */}
        </div>
      )}

      {/* User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). Self-suppresses
          for single-user orgs and for regular users. */}
      <div className="flex items-center justify-end gap-2 mb-2">
        <DownloadDataButton
          entity="notices-it-demand"
          noun="notices"
          selectedIds={selectedNoticeIds}
          selectedRows={(responseOutstandingList || []).filter((r) =>
            selectedNoticeIds.includes(r.id || r.name)
          )}
          fetchAllRecords={fetchAllForExport}
          fileBaseName="noticeai-it-demands"
          title="Outstanding Demands"
        />
        <UserFilterDropdown
          value={userFilter}
          onChange={setUserFilter}
          portal={serviceType}
        />
      </div>

      <TableV2
        columns={columns}
        data={responseOutstandingList}
        itemsPerPage={limit}
        isLoading={loading}
        limitStart={limitStart}
        limit={limit}
        totalRecords={totalRecords}
        type="responseoutstandings"
        rowRedirection={`/${ServiceType.INCOME_TAX}/responseoutstandings-details`}
        selectedIds={selectedNoticeIds}
        setSelectedIds={setSelectedNoticeIds}
        onPageChange={(start) => {
          setLimitStart(start);
          getResponseOutstandingList(start, limit);
        }}
        onLimitChange={handleLimitChange}
        filterValues={filterValues}
        handleFilterValueChange={handleFilterValueChange}
        statusOptions={[]}
        onFetchAllRecords={fetchAllForExport}
      />
      <Modal
        title=""
        open={isDeleteModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        zIndex={9999}
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col">
          <img src={WarningImage} width={250} height={250} />
          <p>Are you sure you want to Delete ?</p>
        </div>
      </Modal>
    </>
  );
};
